import { Loader, useModal, Chevron } from '@jutro/components';
import classNames from 'classnames';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { isCapabilityEnabled } from '@xengage/gw-portals-config-js';
import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    ViewModelForm,
    ViewModelServiceContext,
    withViewModelService,
} from '@xengage/gw-portals-viewmodel-react';
import { IconButton } from '@jutro/legacy/components';
import appConfig from 'app-config';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import { UsersProfileDetailsService } from 'gw-capability-profileinfo';
import _ from 'lodash';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import UserDetailsComponent from '../../components/UserDetailsComponent/UserDetailsComponent';
import messages from './UsersProfile.messages';
import metadata from './UsersProfile.metadata.json5';
import styles from './UsersProfile.module.scss';

const NOT_EDIT_MODE_INDEX = -1;

const hasPoliciesInForce = accountInfo => accountInfo.relatedPolicies.length > 0;

const billingPreferenceValid = account => {
    if(_.isUndefined(account.billingPreference)){
        return true
    }
    
    // Changes to be in sync with On Prem
    // CE - Account - Can't save changes when only an Offer is on the Account
    // OOTB only displays policies, but customization also displays submitted offers.
    // Hide billing preferences on offers since a billing account isn't created until an offer is issued
    if (!account.billingAccountExists || account.billingPreference === 'mail') {
        return true;
    }

    let billingEmailAddress;
    const isBillingAddressSame =
        account.accountSummaryVM.isBillingAddressSame.value;

    if (isBillingAddressSame) {
        billingEmailAddress =
            account.accountSummaryVM.accountContact.emailAddress1.value;
    } else {
        billingEmailAddress =
            account.accountSummaryVM.billingContact.emailAddress1.value;
    }

    return account.billingPreference === 'paperless' && billingEmailAddress;
};

const copyContactProperties = (targetAccountContact, accountContact) => ({
    ...targetAccountContact, cellNumber: accountContact.cellNumber,
    primaryPhoneType: accountContact.primaryPhoneType,
    emailAddress1: accountContact.emailAddress1,
    workNumber: accountContact.workNumber,
    homeNumber: accountContact.homeNumber,
    primaryAddress: {
        addressLine1: accountContact.primaryAddress.addressLine1,
        addressLine2: accountContact.primaryAddress.addressLine2,
        addressLine3: accountContact.primaryAddress.addressLine3,
        city: accountContact.primaryAddress.city,
        postalCode: accountContact.primaryAddress.postalCode,
        state: accountContact.primaryAddress.state,
        country: accountContact.primaryAddress.country,
    },
});

const addressChangedInForm = (updatedAccount, backUpAccountSummary) => {
    const accountAddressSame = _.isEqual(
        backUpAccountSummary.accountContact,
        updatedAccount.accountSummaryVM.accountContact.value
    );
    const billingAddressSame = _.isEqual(
        backUpAccountSummary.billingContact,
        updatedAccount.accountSummaryVM.billingContact.value
    );
    const billingStatusSame = _.isEqual(
        backUpAccountSummary.isBillingAddressSame,
        updatedAccount.accountSummaryVM.isBillingAddressSame.value
    );

    return !(accountAddressSame && billingAddressSame && billingStatusSame);
};

const billingMethodChangedInForm = (
    updatedAccount,
    backupBillingPreference
) => updatedAccount.billingPreference !== backupBillingPreference;

function UsersProfile() {
    const { showConfirm, showAlert } = useModal();

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const authData = useAuthentication()
    const authHeader = _.get(authData, 'authHeader');
    const { onValidate, isComponentValid } =
        useValidation('UsersProfile');
    const [displayLoader, updateDisplayLoader] = useState(true);
    const [displayError, updateDisplayError] = useState(false);
    const [editModeState, updateEditModeState] = useState({
        accountInEditIndex: NOT_EDIT_MODE_INDEX,
    });
    const [isMultiAccount, updateIsMultiAccount] = useState(false);
    const [accountsDetails, updateAccountsDetails] = useState({});

    useEffect(() => {
        if (authHeader) {

        const { capabilitiesConfig } = appConfig;
        const accountInfoPromise =
            UsersProfileDetailsService.getAccountsContactSummaries(authHeader);
        let billingDeliveryPromise = Promise.resolve(null);

        if (
            isCapabilityEnabled({
                capabilitiesConfig,
                capabilityName: 'billing',
            })
        ) {
            billingDeliveryPromise =
                AccountBillingDetailsService.getInvoiceDeliveryMethods(
                    authHeader
                );
        }

        Promise.all([accountInfoPromise, billingDeliveryPromise])
            .then(([accountResult, billingResult]) => {

                const accountSummaries = _.sortBy(
                    accountResult,
                    'accountNumber'
                );
                const billingTypeInfo = _.sortBy(
                    billingResult,
                    'accountNumber'
                );

                const multiAccount = accountSummaries.length > 1;

                updateIsMultiAccount(multiAccount);

                const accountSummariesVM = accountSummaries.map(accountInfo =>
                    viewModelService.create(
                        accountInfo,
                        'pc',
                        'edge.capabilities.profileinfo.user.dto.AccountSummaryDTO'
                    )
                );
                const accounts = accountSummaries.map(
                    (accountSummary, index) => { 
                    let billingAccountExists = false;
                    const billingInfoIndex = billingTypeInfo.findIndex(acct => acct.accountNumber === accountSummary.accountNumber);

                    if (billingInfoIndex > -1) {
                        billingAccountExists = true;
                    }

                        return {
                        billingAccountExists,
                        billingPreference: !_.isEmpty(billingTypeInfo)
                            ? billingTypeInfo[index]?.invoiceDeliveryType
                            : '',
                        accountSummaryVM: accountSummariesVM[index],
                        applyToAllAccounts: false,
                    }
                    }
                );

                updateAccountsDetails({ accounts });
            })
            .finally(() => updateDisplayLoader(false));
        // Only executed on the first render
         
        }
    }, [authHeader, viewModelService]);

    const validateEmailAddress = useCallback(async (emailAddress) => {
        const response = await UsersProfileDetailsService.isProducerEmail(emailAddress, authHeader);

        return response
    }, [authHeader])

    const updateAccounts = useCallback(model => {
        const { accounts: updatedDetails } = model;

        updateAccountsDetails({ accounts: [...updatedDetails] });
    }, []);

    const enterEditMode = useCallback(
        (index) => {           

            const { accounts } = accountsDetails;

            updateEditModeState({
                accountInEditIndex: index,
                backUpAccountSummary: _.cloneDeep(
                    accounts[index].accountSummaryVM.value
                ),
                backupBillingPreference: _.cloneDeep(
                    accounts[index].billingPreference
                ),
            });
        },
        [accountsDetails]
    );

    const resetEditState = useCallback(() => {
        updateEditModeState({
            accountInEditIndex: NOT_EDIT_MODE_INDEX,
        });
        updateDisplayError(false);
    }, []);

    const handleCancel = useCallback(() => {
        showConfirm({
            title: messages.cancelEditTitle,
            message: messages.cancelEditMessage,
            status: 'info',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.yesModel,
            cancelButtonText: commonMessages.noModel,
        }).then(results => {
            if (results === 'confirm') {
                const {
                    accountInEditIndex,
                    backUpAccountSummary,
                    backupBillingPreference,
                } = editModeState;
                const { accounts } = accountsDetails;

                accounts[accountInEditIndex].accountSummaryVM.value =
                    backUpAccountSummary;
                accounts[accountInEditIndex].billingPreference =
                    backupBillingPreference;
                accounts[accountInEditIndex].applyToAllAccounts = false;
                updateAccounts({ accounts });
                resetEditState();
            }
        }, _.noop);
    }, [
        accountsDetails,
        editModeState,
        resetEditState,
        showConfirm,
        updateAccounts,
    ]);

    const updateAllAccounts = useCallback(
        (updatedAccount, updatedAccountIndex, allAccounts) => {
            const updateCalls = [];
            const {
                billingPreference: updatedBillingPreference,
                accountSummaryVM: {
                    value: {
                        accountContact: updatedAccountContact,
                        billingContact: updatedBillingContact,
                        isBillingAddressSame: updatedIsBillingAddressSame,
                    },
                },
            } = updatedAccount;

            const modifiedAccounts = [];

            allAccounts.forEach((account, index) => {
                let accountToBeUpdated;

                if (updatedAccountIndex !== index) {
                    const newAccount = {
                        accountSummaryVM: viewModelService.clone(
                            account.accountSummaryVM
                        ),
                        applyToAllAccounts: false,
                    };

                    newAccount.accountSummaryVM.accountContact.value =
                        copyContactProperties(
                            account.accountSummaryVM.accountContact.value,
                            updatedAccountContact
                        );
                    newAccount.accountSummaryVM.isBillingAddressSame.value =
                        updatedIsBillingAddressSame;
                    newAccount.billingPreference = updatedBillingPreference;

                    if (!updatedIsBillingAddressSame) {
                        newAccount.accountSummaryVM.billingContact.value =
                            copyContactProperties(
                                account.accountSummaryVM.billingContact.value,
                                updatedBillingContact
                            );
                    } else {
                        newAccount.accountSummaryVM.billingContact.value =
                            copyContactProperties(
                                account.accountSummaryVM.billingContact.value,
                                updatedAccountContact
                            );
                    }

                    modifiedAccounts.push(newAccount);
                    accountToBeUpdated = newAccount;
                } else {
                    modifiedAccounts.push(updatedAccount);
                    accountToBeUpdated = updatedAccount;
                }

                updateCalls.push(
                    UsersProfileDetailsService.updateAccountContactSummary(
                        accountToBeUpdated.accountSummaryVM.value,
                        authHeader
                    )
                );
                updateCalls.push(
                    AccountBillingDetailsService.setInvoiceDeliveryMethodForAccount(
                        updatedBillingPreference,
                        accountToBeUpdated.accountSummaryVM.value.accountNumber,
                        authHeader
                    )
                );
            });

            Promise.all(updateCalls)
                .then(
                    () => {
                        modifiedAccounts[
                            updatedAccountIndex
                        ].applyToAllAccounts = false;
                        updateAccounts({ accounts: modifiedAccounts });
                        resetEditState();
                    },
                    () => {
                        updateDisplayError(true);
                    }
                )
                .finally(() => updateDisplayLoader(false));
        },
        [authHeader, resetEditState, updateAccounts, viewModelService]
    );

    const updateContactDetails = useCallback(() => {
        const {
            accountInEditIndex,
            backupBillingPreference,
            backUpAccountSummary,
        } = editModeState;
        const { accounts } = accountsDetails;
        const updatedAccountDetails = accounts[accountInEditIndex];
        const { capabilitiesConfig } = appConfig;
        const isBillingEnabled = isCapabilityEnabled({
            capabilitiesConfig,
            capabilityName: 'billing',
        });

        if (
            isBillingEnabled &&
            !billingPreferenceValid(updatedAccountDetails)
        ) {
            showAlert({
                title: translator(messages.inValidPaperlessTitle),
                message: translator(messages.inValidPaperlessMessage),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            }).catch(_.noop);

            return;
        }

        updateDisplayLoader(true);

        if (updatedAccountDetails.applyToAllAccounts) {
            updateAllAccounts(
                updatedAccountDetails,
                accountInEditIndex,
                accounts
            );

            return;
        }

        const updateCalls = [];

        if (addressChangedInForm(updatedAccountDetails, backUpAccountSummary)) {            
            updateCalls.push(() =>
                UsersProfileDetailsService.updateAccountContactSummary(
                    updatedAccountDetails.accountSummaryVM.value,
                    authHeader
                )
            );
        }

        if (
            billingMethodChangedInForm(
                updatedAccountDetails,
                backupBillingPreference
            )
        ) {
            updateCalls.push(() =>
                AccountBillingDetailsService.setInvoiceDeliveryMethodForAccount(
                    updatedAccountDetails.billingPreference,
                    updatedAccountDetails.accountSummaryVM.value.accountNumber,
                    authHeader
                )
            );
        }

        validateEmailAddress(accounts[accountInEditIndex].accountSummaryVM.value, authHeader).then((response) => {
            if(!response){
                Promise.all(updateCalls.map(fn => fn()))
                .then(
                    () => {
                        resetEditState();
                    },
                    () => {
                        updateDisplayError(true);
                    }
                )
                .finally(() => updateDisplayLoader(false));
            } else {
                updateDisplayLoader(false)
                showAlert({
                    title: translator(messages.emailInvalidTitle),
                    message: translator(messages.emailInvalidMessage),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
                
            }
            
            
        })

        
    }, [editModeState, accountsDetails, validateEmailAddress, showAlert, translator, updateAllAccounts, authHeader, resetEditState]);

    const getAccountTitle = useCallback(
        accountSummaryVM => {
            const accountNumber = accountSummaryVM.accountNumber.value;
            const accountTitle = translator(messages.accountTitleWithNumber, {
                accountNumber,
            })

            if (isMultiAccount && hasPoliciesInForce(accountSummaryVM)) {
                // 2025-02-18 - Debra Frum - DE18445
                // Add a space after commma delimiter so that list of many policies will wrap
                const policyNumbers = accountSummaryVM?.relatedPolicies?.value.join(', ')
            
                return `${accountTitle}. ${translator(messages.accountTitleWithNumberInforcePolicies, { policyNumbers})}`;
            }

            if (isMultiAccount && !hasPoliciesInForce(accountSummaryVM)) {
                return `${accountTitle}. ${translator(messages.accountTitleWithNumberNoInforcePolicies)}`;
            }

            return accountTitle;
        },
        [isMultiAccount, translator]
    );

    const isReadOnly = useCallback(
        index => editModeState.accountInEditIndex !== index,
        [editModeState.accountInEditIndex]
    );

    const renderAccordionHeader = useCallback(
        (isOpen, accountSummaryVM, index) => {
            const headerStyle = isOpen ? classNames(styles.accordionHeader, styles.isOpen) : styles.accordionHeader
            
            return (
                <div className={headerStyle}>
                    <div className={styles.accordionHeaderTitle}>
                        <h3>
                            {getAccountTitle(accountSummaryVM)}
                        </h3>
                    </div>
                    <div className={styles.iconContainer}>
                        { editModeState.accountInEditIndex === NOT_EDIT_MODE_INDEX && <IconButton                          
                            icon="mi-edit"
                            onClick={event => {
                                try{
                                    event.preventDefault();
                                    event.stopPropagation();
                                }
                                catch(ex){
                                    _.noop()
                                }

                                enterEditMode(
                                    index
                                );
                            }}
                        /> }
                        <Chevron
                            isOpen={isOpen}
                        />
                    </div>

                </div>
            )
        },
        [editModeState, enterEditMode, getAccountTitle]
    );

    const generatedProps = useMemo(() => {
        const { accounts = [] } = accountsDetails;
        const overrides = accounts.map(({ accountSummaryVM }, index) => ({
            [`accountItemContainer${index}`]: {
                renderHeader: isOpen =>
                    renderAccordionHeader(isOpen, accountSummaryVM, index),
                initialExpanded: index === 0
            },
            [`editButton${index}`]: {
                visible:
                    editModeState.accountInEditIndex ===
                    NOT_EDIT_MODE_INDEX,
            },
            [`errorMsg${index}`]: {
                visible: displayError && !isReadOnly(index),
            },
            [`accountContactContainer${index}`]: {
                readOnly: isReadOnly(index),
                validateEmailAddress
            },            
            [`actionsContainer${index}`]: {
                visible: !isReadOnly(index),
            },
            [`saveButton${index}`]: {
                disabled: !isComponentValid,
            }
        }));

        return Object.assign({}, ...overrides);
    }, [accountsDetails, editModeState?.accountInEditIndex, displayError, isReadOnly, validateEmailAddress, isComponentValid, renderAccordionHeader]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: false,
            phonewide: {
                labelPosition: 'top',
            },
            phone: {
                labelPosition: 'top',
            },
        },
        singleAccountContainer: {
            visible: !isMultiAccount
        },
        accountsIterable:{
            visible: isMultiAccount,
            defaultOpenedId: `accountItemContainer${editModeState.accountInEditIndex !== -1 ? editModeState.accountInEditIndex : 0}accountsIterable`,
        },
        titleAccount: {
            content: accountsDetails.accounts && accountsDetails.accounts.length > 0 
            ? getAccountTitle(accountsDetails.accounts[0].accountSummaryVM) 
            : []
        },
        editButton: {
            visible:
                editModeState.accountInEditIndex ===
                NOT_EDIT_MODE_INDEX,
            onClick: (evt) => {
                try{
                    evt.preventDefault();
                    evt.stopPropagation();
                }
                catch(ex){
                    _.noop()
                }

                enterEditMode(0);
            }
        },
        errorMsg: {
            visible: displayError && !isReadOnly(0),
        },
        accountContactContainer: {
            readOnly: isReadOnly(0),
            validateEmailAddress
        },            
        actionsContainer: {
            visible: !isReadOnly(0),
        },
        saveButton: {
            disabled: !isComponentValid,
        },
        ...generatedProps,
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            enterEditMode,
            handleCancel,
            updateContactDetails,
            onValidate
        },
        resolveComponentMap: {
            userdetailscomponent: UserDetailsComponent
        },
    };

    if (displayLoader) {
        return <Loader />;
    }

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={accountsDetails}
            overrideProps={overrideProps}
            onModelChange={updateAccounts}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default withViewModelService(UsersProfile);
